<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card dark outlined>
          <v-card-title class="secondary py-1 text-subtitle-1">
            <v-icon left dense>mdi-filter</v-icon>
            {{ header }}
            <v-spacer v-if="headerButtonText || secondHeaderButtonText" />
            <v-btn
              v-if="headerButtonText"
              elevation="2"
              class="mr-2"
              x-small
              :disabled="isHeaderButtonBusy"
              @click="onHeaderButtonClick"
            >
              <v-progress-circular
                v-if="isHeaderButtonBusy"
                indeterminate
                size="12"
                width="2"
                class="mr-1"
              />
              <v-icon v-else left x-small>mdi-download</v-icon>
              {{ isHeaderButtonBusy ? "Generating…" : headerButtonText }}
            </v-btn>
            <v-btn
              v-if="secondHeaderButtonText"
              elevation="2"
              class="mr-2"
              x-small
              @click="secondHeaderButtonAction"
            >
              <v-icon left x-small>mdi-download</v-icon>
              {{ secondHeaderButtonText }}
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-row>
              <global-dateTimeRangePicker @event-changed="handleEventChanged" />

              <global-stallholderFilterGroup
                v-if="showStallholders"
                ref="stallholderFilterGroup"
                :showTerminals="showTerminals"
                :singleSelect="transactionsMode"
                @event-changed="handleEventChanged"
              />
              <v-spacer v-else />
              <v-col
                cols="12"
                :md="showStallholders ? '12' : '3'"
                lg="3"
                class="text-right"
              >
                <div class="d-flex justify-end flex-wrap">
                  <v-btn
                    color="secondary"
                    class="mt-4 white--text mr-2"
                    :disabled="isTodayButtonDisabled"
                    @click="setToday"
                  >
                    Today
                    <v-icon dark right>mdi-calendar-today</v-icon>
                  </v-btn>
                  <v-btn
                    color="secondary"
                    class="mt-4 white--text mr-2"
                    @click="clear"
                  >
                    Clear
                    <v-icon dark right>mdi-filter-off</v-icon>
                  </v-btn>
                  <v-btn
                    color="secondary"
                    class="mt-4 white--text"
                    @click="handleClickSubmit"
                  >
                    Submit
                    <v-icon right dark>mdi-magnify</v-icon>
                  </v-btn>
                </div>
              </v-col>
            </v-row>
            <v-row v-if="transactionsMode || showTransactionFilters">
              <v-col cols="12">
                <v-expand-transition>
                  <div v-if="transactionsMode || showTransactionFilters">
                    <v-row align="center">
                      <v-col cols="12" md="3">
                        <v-text-field
                          v-model="deviceId"
                          label="Device ID"
                          dense
                          clearable
                          :disabled="modeCActive"
                          hint="Mode B — with STAN and/or Amount"
                          persistent-hint
                          @click:clear="clearDeviceId"
                        />
                      </v-col>
                      <v-col cols="12" md="2">
                        <v-text-field
                          v-model="stan"
                          label="STAN"
                          dense
                          clearable
                          :disabled="modeCActive || !deviceId"
                          @click:clear="clearStan"
                        />
                      </v-col>
                      <v-col cols="12" md="2">
                        <v-text-field
                          v-model="totalAmount"
                          label="Total Amount"
                          dense
                          clearable
                          prefix="$"
                          :disabled="modeCActive || !deviceId"
                          @click:clear="clearTotalAmount"
                        />
                      </v-col>
                      <v-col cols="12" md="1" class="text-center"> or </v-col>
                      <v-col cols="12" md="3">
                        <v-text-field
                          v-model="transactionId"
                          label="Transaction ID"
                          dense
                          clearable
                          :disabled="modeBActive"
                          hint="Mode C — partial match"
                          persistent-hint
                          @click:clear="clearTransactionId"
                        />
                      </v-col>
                    </v-row>
                  </div>
                </v-expand-transition>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import moment from "moment";

export default {
  props: {
    header: {
      type: String,
      default: null,
    },
    showStallholders: {
      type: Boolean,
      default: false,
    },
    showTerminals: {
      type: Boolean,
      default: false,
    },
    headerButtonText: {
      type: String,
      default: null,
    },
    headerButtonAction: {
      type: Function,
      default: null,
    },
    headerButtonLoading: {
      type: Boolean,
      default: false,
    },
    secondHeaderButtonText: {
      type: String,
      default: null,
    },
    secondHeaderButtonAction: {
      type: Function,
      default: null,
    },
    showTransactionFilters: {
      type: Boolean,
      default: false,
    },
    /** Exclusive A/B/C filter modes for Transactions page */
    transactionsMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      deviceId: "",
      stan: "",
      totalAmount: null,
      transactionId: "",
      localHeaderButtonLoading: false,
    };
  },
  computed: {
    ...mapState({
      globalEventFilter: (state) => state.filter.globalEventFilter,
    }),
    isHeaderButtonBusy() {
      return !!(this.headerButtonLoading || this.localHeaderButtonLoading);
    },
    modeCActive() {
      return this.transactionsMode && !!(this.transactionId || "").trim();
    },
    modeBActive() {
      return (
        this.transactionsMode &&
        !!(this.deviceId || "").trim() &&
        !this.modeCActive
      );
    },
    modeBOrCActive() {
      return this.modeBActive || this.modeCActive;
    },
    isTodayButtonDisabled() {
      if (!this.globalEventFilter || !this.globalEventFilter.endDateTime) {
        return false;
      }

      const eventEndDate = moment(
        this.globalEventFilter.endDateTime,
        "YYYYMMDDHHmm"
      );
      const threeDaysAgo = moment().subtract(3, "days");

      return eventEndDate.isBefore(threeDaysAgo);
    },
  },
  watch: {
    headerButtonLoading(val) {
      // Parent finished (or never started) — clear optimistic local busy state
      if (!val) {
        this.localHeaderButtonLoading = false;
      }
    },
    deviceId(newVal) {
      this.setDeviceId(newVal || "");
      if (this.transactionsMode && (newVal || "").trim()) {
        this.transactionId = "";
        this.setTransactionId("");
      }
    },
    stan(newVal) {
      this.setStan(newVal || "");
    },
    totalAmount(newVal) {
      this.setTotalAmount(newVal || null);
    },
    transactionId(newVal) {
      this.setTransactionId(newVal || "");
      if (this.transactionsMode && (newVal || "").trim()) {
        this.deviceId = "";
        this.stan = "";
        this.totalAmount = null;
        this.setDeviceId("");
        this.setStan("");
        this.setTotalAmount(null);
      }
    },
  },
  mounted() {
    // Sync local fields from Vuex if already set
    this.deviceId = this.$store.state.filter.deviceId || "";
    this.stan = this.$store.state.filter.stan || "";
    this.totalAmount = this.$store.state.filter.totalAmount;
    this.transactionId = this.$store.state.filter.transactionId || "";
  },
  methods: {
    ...mapActions("dashboard", ["updateDashboard"]),
    ...mapMutations("filter", [
      "setSearchStartTime",
      "setSearchEndTime",
      "setDeviceId",
      "setStan",
      "setTotalAmount",
      "setTransactionId",
      "setSearchStallholders",
      "setSearchStalls",
      "setSearchTerminals",
    ]),
    onHeaderButtonClick() {
      if (this.isHeaderButtonBusy) return;
      // Show busy immediately (don't wait on parent prop)
      this.localHeaderButtonLoading = true;
      try {
        const result =
          typeof this.headerButtonAction === "function"
            ? this.headerButtonAction()
            : null;
        Promise.resolve(result).finally(() => {
          this.localHeaderButtonLoading = false;
        });
      } catch (e) {
        this.localHeaderButtonLoading = false;
        throw e;
      }
    },
    setToday() {
      this.setSearchStartTime(moment().format("YYYYMMDD") + "0000");
      this.setSearchEndTime(moment().format("YYYYMMDD") + "2359");
      if (!this.transactionsMode) {
        this.updateDashboard("EFM setToday");
      }
    },
    clear() {
      // Reset from/to to event start/end (Aaron) — shared across pages
      const event = this.globalEventFilter || {};
      if (event.startDateTime && event.endDateTime) {
        this.setSearchStartTime(event.startDateTime);
        this.setSearchEndTime(event.endDateTime);
      } else {
        this.setSearchStartTime("notSet");
        this.setSearchEndTime("notSet");
      }
      this.clearDeviceId();
      this.clearStan();
      this.clearTotalAmount();
      this.clearTransactionId();
      if (this.showStallholders && this.$refs.stallholderFilterGroup) {
        this.$refs.stallholderFilterGroup.clearSelections();
      } else if (this.showStallholders) {
        this.setSearchStallholders("notSet");
        this.setSearchStalls("notSet");
        this.setSearchTerminals("notSet");
      }
      if (this.transactionsMode) {
        this.$emit("cleared");
      } else if (this.showStallholders) {
        this.$emit("cleared");
        this.updateDashboard("EFM clear");
      }
    },
    clearDeviceId() {
      this.deviceId = "";
      this.setDeviceId("");
    },
    clearStan() {
      this.stan = "";
      this.setStan("");
    },
    clearTotalAmount() {
      this.totalAmount = null;
      this.setTotalAmount(null);
    },
    clearTransactionId() {
      this.transactionId = "";
      this.setTransactionId("");
    },
    handleEventChanged() {
      // this.updateDashboard("EFM globalEventFilter.EventId");
    },
    handleClickSubmit() {
      if (this.transactionsMode) {
        this.$emit("submit");
        return;
      }
      this.updateDashboard("EFM handleClickSubmit");
    },
  },
};
</script>

<style scoped>
.v-card {
  margin-bottom: 20px;
}
</style>
